import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Mail, Lock } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import Loader from "@/components/ui/loader";
import { cn } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';

interface LoginFormContentProps {
  onShowResetPassword: () => void;
}

const LoginFormContent = ({ onShowResetPassword }: LoginFormContentProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const { signIn } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast({
        title: t('login.missingInfoTitle'),
        description: t('login.missingInfoDesc'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await signIn(loginData.email, loginData.password);
      
      if (error) {
        let errorMessage = t('login.loginFailedGeneric');

        if (error.message.includes("Invalid login credentials")) {
          errorMessage = t('login.invalidCredentials');
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = t('login.emailNotConfirmed');
        } else if (error.message.includes("Too many requests")) {
          errorMessage = t('login.tooManyRequests');
        }

        toast({
          title: t('login.loginFailedTitle'),
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        // Login riuscito: naviga a /dashboard, ApprovalRoute gestisce il redirect per ruolo/stato
        navigate('/dashboard');
      }
    } catch (err) {
      toast({
        title: t('login.loginErrorTitle'),
        description: t('login.loginErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-4 lg:space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
          {t('auth.email')}
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="email" 
            type="email" 
            value={loginData.email}
            onChange={(e) => setLoginData({...loginData, email: e.target.value})}
            className={cn(
              "border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pl-10",
              "h-11 lg:h-12"
            )}
            placeholder={t('login.emailPlaceholder')}
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm font-medium text-gray-700">
          {t('auth.password')}
        </Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            id="password" 
            type="password" 
            value={loginData.password}
            onChange={(e) => setLoginData({...loginData, password: e.target.value})}
            className={cn(
              "border-gray-200 focus:border-[#030A14] focus:ring-[#030A14] pl-10",
              "h-11 lg:h-12"
            )}
            placeholder={t('login.passwordPlaceholder')}
            required 
            disabled={isLoading}
          />
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <button 
          type="button"
          onClick={onShowResetPassword}
          className="text-[#030A14] hover:text-[#030A14]/80 hover:underline font-medium"
        >
          {t('auth.forgotPassword')}
        </button>
      </div>
      
      <Button 
        type="submit" 
        className={cn(
          "w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200",
          "h-11 lg:h-12"
        )}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader size="sm" className="mr-3" />
            {t('login.signingIn')}
          </>
        ) : (
          <>
            <LogIn className="w-5 h-5 mr-2" />
            {t('login.signInButton')}
          </>
        )}
      </Button>
    </form>
  );
};

export default LoginFormContent; 